import { useEffect, useRef, useState } from "react";
import SC from "./style.js";
import axios from "axios";
import { readFromLocalStorage } from "utils/helper/index.js";
import { apiPost } from "api/callApi.js";
import { useSearchParams } from "react-router-dom";
import InnoIcons from "@InnoIcons/";
import ChatMessage from "InnoUI/components/ChatMessage/index.jsx";
import { queryTypes } from "utils/lookUpTable/index.js";

const InteractiveSearchUI = (props) => {
  const {
    handleIntractiveSearch = () => {},
    setChatData = () => {},
    jobType,
    queryType,
    border = true,
    IsHideCloseIcon = false,
  } = props;
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatToken, setChatToken] = useState(null);
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const type = searchParams.get("type");
  const [isTokenLoading, setIsTokenLoading] = useState(false);

  const {
    REACT_APP_BASE_URL: baseURL,
    REACT_APP_REQUEST_TIMEOUT: timeout,
    REACT_APP_API_KEY,
  } = process.env;

  let accessToken = REACT_APP_API_KEY;
  let userAuth = readFromLocalStorage("userAccessToken");
  let BASE_URL = baseURL || `${window.location.origin}/api`;
  userAuth = userAuth === null ? "" : userAuth;
  const messagesEndRef = useRef(null);

  const requestTimeout = timeout ? parseInt(timeout, 10) * 1000 : 180000;

  const getToken = async () => {
    try {
      setIsTokenLoading(true);
      const result = await apiPost(`/v1/ai/token/${projectId}/${type}`);
      setChatToken(result);
    } catch (error) {
      console.log("Error", error);
    } finally {
      setIsTokenLoading(false);
    }
  };

  useEffect(() => {
    getToken();
  }, [projectId, type]);

  const handleRefreshSession = async () => {
    setMessages([]);
    setInputValue("");
    setLoading(false);
    setChatData(null);

    await getToken();
  };

  const handleSend = async () => {
    const question = inputValue.trim();
    if (!question) return;

    if (!chatToken) {
      getToken();
      return;
    }

    const userMessage = {
      id: Date.now() + "_q",
      from: "user",
      question,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setLoading(true);

    const url = BASE_URL + `/v1/ai/response`;

    try {
      const res = await axios.post(
        url,
        {
          query: question,
          sessionId: chatToken?.sessionId,
          queryType,
        },
        {
          headers: {
            "Content-Type": "application/json",
            api_key: accessToken,
            Authorization: `Bearer ${userAuth}`,
            token: chatToken?.token,
          },
          timeout: requestTimeout,
        },
      );

      const apiJson = res.data;

      const apiAnswer =
        apiJson?.answer ??
        "No answer found. Please try again with a different question.";

      const isJournal = queryType === queryTypes.journal;
      const dataRef = apiJson?.dataRef;
      const bulletsSource = isJournal ? dataRef?.journal : dataRef?.files;

      const bullets = Array.isArray(bulletsSource)
        ? isJournal
          ? bulletsSource
          : bulletsSource.map((f) => `File ID: ${f}`)
        : [];

      const assistantMessage = {
        id: Date.now() + "_a",
        from: "assistant",
        response: {
          title: apiJson?.message ?? "Response",
          text: apiAnswer,
          evidence: bullets.length ? "Data references:" : "",
          bullets,
        },
      };

      setMessages((prev) => [...prev, assistantMessage]);

      if (!apiJson?.followUp) {
        setChatData(dataRef);
      }
    } catch (err) {
      console.error("API error:", err);

      const errorMessage = {
        id: Date.now() + "_err",
        from: "assistant",
        response: {
          title: "Error",
          text:
            err?.response?.data?.message ||
            "Sorry, something went wrong while fetching the answer.",
          evidence: "",
          bullets: [],
        },
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  const scrollToBottom = (behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({
      behavior,
      // block: "end",
    });
  };

  useEffect(() => {
    if (loading) {
      scrollToBottom("smooth");
    }
  }, [loading]);

  const [copiedMessageId, setCopiedMessageId] = useState(null);

  const handleCopy = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);

      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  return (
    <SC.ChatContainer className={messages.length === 0 ? "empty" : ""}>
      <SC.Header border={border}>
        <SC.HeaderTitle>Interactive search</SC.HeaderTitle>

        <SC.HeaderActions>
          <SC.RefreshButton
            onClick={handleRefreshSession}
            title="Start a New Chat"
          >
            {/* <InnoIcons.RefreshIcon /> */}
            New Chat
          </SC.RefreshButton>

          {!IsHideCloseIcon && (
            <SC.CloseButton aria-label="Close" onClick={handleIntractiveSearch}>
              <InnoIcons.CloseIcon />
            </SC.CloseButton>
          )}
        </SC.HeaderActions>
      </SC.Header>

      {/* Messages area with ref */}
      <SC.MessagesArea>
        {messages.length === 0 ? (
          <SC.EmptyState>
            <SC.EmptyStateText>
              What would you like to search for?
            </SC.EmptyStateText>
          </SC.EmptyState>
        ) : (
          <>
            {messages.map((message) => (
              <div key={message.id}>
                {message.from === "user" && (
                  <SC.QuestionWrapper>
                    <SC.QuestionText>{message.question}</SC.QuestionText>
                  </SC.QuestionWrapper>
                )}

                {message.from === "assistant" && message.response && (
                  <SC.AnswerWrapper>
                    <SC.MessageBubble>
                      <SC.CopyWrapper>
                        <SC.CopyButton
                          onClick={() =>
                            handleCopy(message.response.text, message.id)
                          }
                        >
                          {copiedMessageId === message.id ? (
                            "Copied ✓"
                          ) : (
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <InnoIcons.CopyIcon sx={{ fontSize: "small" }} />
                            </div>
                          )}
                        </SC.CopyButton>
                      </SC.CopyWrapper>

                      <SC.ResponseText>
                        <ChatMessage text={message.response.text} />
                      </SC.ResponseText>
                    </SC.MessageBubble>
                  </SC.AnswerWrapper>
                )}
              </div>
            ))}
          </>
        )}

        {loading && (
          <SC.TypingWrapper>
            <SC.TypingBubble>
              <SC.Dot />
              <SC.Dot />
              <SC.Dot />
            </SC.TypingBubble>
          </SC.TypingWrapper>
        )}
        <div ref={messagesEndRef} />
      </SC.MessagesArea>

      <SC.InputContainer>
        <SC.InputWrapper>
          <SC.Input
            type="text"
            placeholder="Type Something..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            aria-label="Type your question"
            disabled={loading || isTokenLoading}
          />

          <SC.SendButton
            onClick={handleSend}
            disabled={!inputValue.trim() || loading || isTokenLoading}
            aria-label="Send"
          >
            <InnoIcons.SendIcon sx={{ color: "white", fontSize: "20px " }} />
          </SC.SendButton>
        </SC.InputWrapper>
      </SC.InputContainer>
    </SC.ChatContainer>
  );
};

export default InteractiveSearchUI;
